import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { IMG } from "@/mock";
import { setSession } from "@/lib/store";
import { api, apiError } from "@/lib/api";

export default function Auth() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);
  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/w/projects";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const sendReset = async (e) => {
    e.preventDefault(); setErr("");
    if (!email.includes("@")) return setErr("Enter your account email first");
    setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); setSent(true); toast.success(data.message); }
    catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  const submit = (mode) => async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.includes("@")) return setErr("Enter a valid email");
    if (pw.length < 6) return setErr("Password must be at least 6 characters");
    setBusy(true);
    try {
      const { data } = await api.post(mode === "in" ? "/auth/login" : "/auth/register", mode === "in" ? { email, password: pw } : { email, password: pw, name });
      setSession(data);
      toast.success(mode === "in" ? `Welcome back, ${data.user.name}!` : "Account created — let's lock in.");
      nav("/w/projects");
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };
  const field = "bg-[#16181b] border-white/5 h-11 focus-visible:ring-amber-400/60";
  return (
    <div className="min-h-screen bg-[#0b0b0c] flex flex-col items-center py-9 px-4">
      <img src={IMG.logo} alt="In The Pocket" className="h-32 w-auto fade-up" />
      <p className="text-zinc-400 mt-4">Stay in the groove, stay in the pocket</p>
      <div className="w-full max-w-[466px] mt-9 rounded-xl border border-white/10 bg-[#101113] overflow-hidden fade-up">
        <Tabs defaultValue="in" onValueChange={() => setErr("")}>
          <TabsList className="w-full grid grid-cols-2 bg-[#16181b] rounded-none h-11 p-1">
            <TabsTrigger value="in" data-testid="tab-signin" className="data-[state=active]:bg-[#0b0b0c]">Sign In</TabsTrigger>
            <TabsTrigger value="up" data-testid="tab-signup" className="data-[state=active]:bg-[#0b0b0c]">Sign Up</TabsTrigger>
          </TabsList>
          {["in", "up"].map((m) => (
            <TabsContent key={m} value={m} className="p-6 mt-0">
              {m === "in" && forgot ? (
                <form onSubmit={sendReset} className="space-y-5" data-testid="forgot-form">
                  <div><h2 className="text-2xl font-light">Reset your password</h2><p className="text-sm text-zinc-500 mt-1">We'll email you a secure link that works for one hour.</p></div>
                  <div className="space-y-2"><Label>Email</Label><Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={field} /></div>
                  {err && <div data-testid="auth-error" className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">{err}</div>}
                  {sent && <div data-testid="forgot-sent" className="text-sm text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-lg px-3 py-2">Check your inbox — if that email has an account, a reset link is on its way.</div>}
                  <button type="submit" disabled={busy || sent} data-testid="forgot-submit" className="w-full h-11 rounded-lg bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-zinc-900 font-medium flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Send reset link</button>
                  <button type="button" onClick={() => { setForgot(false); setSent(false); setErr(""); }} data-testid="forgot-back" className="text-sm text-zinc-400 hover:text-white">← Back to sign in</button>
                </form>
              ) : (<>
              <h2 className="text-2xl font-light">{m === "in" ? "Welcome back" : "Create your account"}</h2>
              <p className="text-sm text-zinc-500 mt-1">{m === "in" ? "Sign in to access your songs, artwork and setlists" : "Your work syncs to every device you sign in on"}</p>
              <form onSubmit={submit(m)} className="space-y-5 mt-7">
                {m === "up" && <div className="space-y-2"><Label>Name</Label><Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your stage name" className={field} /></div>}
                <div className="space-y-2"><Label>Email</Label><Input data-testid={`auth-email-${m}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={field} /></div>
                <div className="space-y-2"><Label>Password</Label><Input data-testid={`auth-password-${m}`} type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" className={field} /></div>
                {err && <div data-testid="auth-error" className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">{err}</div>}
                {m === "in" && <button type="button" onClick={() => { setForgot(true); setErr(""); }} data-testid="forgot-link" className="text-sm text-amber-400 hover:text-amber-300">Forgot password?</button>}
                <button type="submit" disabled={busy} data-testid={`auth-submit-${m}`} className="w-full h-11 rounded-lg bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-zinc-900 font-medium transition-colors flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}{m === "in" ? "Sign In" : "Create Account"}
                </button>
              </form>
              <div className="flex items-center gap-3 my-5 text-[11px] uppercase tracking-[.2em] text-zinc-500"><span className="flex-1 h-px bg-white/10" />or<span className="flex-1 h-px bg-white/10" /></div>
              <button type="button" onClick={googleSignIn} data-testid={`google-signin-${m}`} className="w-full h-11 rounded-lg border border-white/10 bg-white/[.03] hover:bg-white/[.08] text-sm font-medium flex items-center justify-center gap-3 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.7 1.2 9.2 3.6l6.9-6.9C35.9 2.3 30.3 0 24 0 14.6 0 6.5 5.4 2.5 13.2l8 6.2C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-8-6.2A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.8l8-6.2z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.8l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-4.1-13.5-9.9l-8 6.2C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google
              </button>
              </>)}
            </TabsContent>
          ))}
        </Tabs>
      </div>
      <p className="text-sm text-zinc-500 mt-6">By signing up, you agree to our terms of service</p>
      <Link to="/" className="text-sm text-amber-400 mt-4 hover:text-amber-300">← Back to home</Link>
    </div>
  );
}
